import { router } from "@inertiajs/react";
import { BackofficeLayout } from "@/components/layout/BackofficeLayout";
import { SearchInput } from "@/components/shared/SearchInput";
import { Pagination, PaginationMeta } from "@/components/shared/Pagination";
import { Briefcase } from "lucide-react";
import { formatDate } from "@/lib/utils";

type Business = {
  id: string;
  name: string;
  email: string;
  account_name: string;
  account_slug: string;
  created_at: string;
};

type Props = {
  businesses: Business[];
  search: string;
  pagination: PaginationMeta;
};

export default function BackofficeBusinessesIndex({
  businesses,
  search,
  pagination,
}: Props) {
  return (
    <BackofficeLayout
      title="Businesses"
      breadcrumbs={[
        { label: "Backoffice", href: "/backoffice/dashboard" },
        { label: "Businesses" },
      ]}
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Businesses</h1>
          <p className="text-sm text-muted-foreground">
            {pagination.total} total
          </p>
        </div>
      </div>

      <SearchInput value={search} placeholder="Search businesses..." />

      <div className="mt-4">
        {businesses.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <Briefcase className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">
              No businesses found
            </h3>
            <p className="text-sm text-muted-foreground">
              Businesses will appear here as users create them.
            </p>
          </div>
        ) : (
          <div className="border rounded-lg">
            {businesses.map((b, i) => (
              <div
                key={b.id}
                className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors ${i > 0 ? "border-t" : ""}`}
                onClick={() => router.visit(`/backoffice/businesses/${b.id}`)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <span className="text-xs font-medium text-muted-foreground">
                      {b.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">
                      {b.name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {b.email} · {b.account_name}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground shrink-0 ml-4">
                  {formatDate(b.created_at)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4">
        <Pagination pagination={pagination} />
      </div>
    </BackofficeLayout>
  );
}
